<script setup lang="ts">
import { Planungsgeschaeft } from '@/models/Planungsgeschaeft';
import { RouterLink } from 'vue-router';
import { useAuthUser, useGlobals } from '@/helpers/globals';
import { inject, type Ref } from 'vue';

const props = defineProps<{
    geschaeft: Planungsgeschaeft;
    saving: boolean;
}>();

const globals = useGlobals();

// injected from PlanungsgeschaeftEditView
const readonly = inject('readonly') as Ref<boolean>;

const emit = defineEmits<{
    'geschaeft-changed': [section: string | null];
}>();

const authUser = useAuthUser();

async function onIdentEdit(event: Event) {
    if (!event.target || readonly.value) {
        return;
    }
    const target = event.target as HTMLElement;
    let txt = target.innerHTML.replace(/(\r\n|\n|\r)/gm, ' ');

    if (txt === props.geschaeft.get('description')) {
        return;
    }
    txt = txt.substr(0, 100);
    props.geschaeft.set('description', txt);
    target.innerHTML = txt;
    emit('geschaeft-changed', 'allgemeine_angaben');
}
</script>

<template>
    <div>
        <h2
            class="ktg-title-02 mt-2_5rem"
            :contenteditable="readonly ? 'false' : 'plaintext-only'"
            @blur="onIdentEdit"
        >
            {{ geschaeft.description ?? '(' + $t('unbenanntes_geschaeft') + ')' }}
        </h2>
        <ktg-tabs appearance="secondary" class="mt-2_5rem mb-2_5rem">
            <RouterLink
                :to="{ name: 'planungsgeschaeft-edit-dashboard', params: { id: props.geschaeft.id } }"
                custom
                v-slot="{ isExactActive, navigate }"
            >
                <ktg-tab :active="isExactActive || null" @activate="navigate">{{ $t('dashboard') }}</ktg-tab>
            </RouterLink>
            <RouterLink
                :to="{ name: 'planungsgeschaeft-edit-editor', params: { id: props.geschaeft.id } }"
                custom
                v-slot="{ isExactActive, navigate }"
            >
                <ktg-tab :active="isExactActive || null" @activate="navigate">{{ $t('editor') }}</ktg-tab>
            </RouterLink>
            <RouterLink
                v-if="authUser?.isRoleAdmin || authUser?.isRoleGemeinde"
                :to="{ name: 'planungsgeschaeft-edit-documents', params: { id: props.geschaeft.id } }"
                custom
                v-slot="{ isExactActive, navigate }"
            >
                <ktg-tab :active="isExactActive || null" @activate="navigate">{{ $t('documents') }}</ktg-tab>
            </RouterLink>
            <RouterLink
                v-if="authUser?.isRoleAdmin"
                :to="{ name: 'planungsgeschaeft-edit-access', params: { id: props.geschaeft.id } }"
                custom
                v-slot="{ isExactActive, navigate }"
            >
                <ktg-tab :active="isExactActive || null" @activate="navigate">{{ $t('berechtigungen') }}</ktg-tab>
            </RouterLink>
            <RouterLink
                v-if="authUser?.isRoleAdmin || globals.environment !== 'prod'"
                :to="{ name: 'planungsgeschaeft-edit-ech', params: { id: props.geschaeft.id } }"
                custom
                v-slot="{ isExactActive, navigate }"
            >
                <ktg-tab :active="isExactActive || null" @activate="navigate">{{ $t('messages') }}</ktg-tab>
            </RouterLink>
        </ktg-tabs>
        <RouterView
            :geschaeft="props.geschaeft"
            :saving="props.saving"
            @geschaeft-changed="$emit('geschaeft-changed', $event)"
        ></RouterView>
    </div>
</template>
